/**
 * errorReporting — intercepta erros de runtime do frontend e publica em ntfy.sh.
 *
 * Ativo apenas quando a variante de build define VITE_NTFY_ERRORS_TOPIC
 * (doutor/ohmybox; New Shoes fica de fora até decidirem). Captura:
 *   - window 'error' (erros não tratados, incl. de scripts)
 *   - window 'unhandledrejection' (promises rejeitadas sem catch)
 *   - app.config.errorHandler do Vue (erros de componente/render)
 *
 * Anti-tempestade: dedupe por mensagem (janela de 5 min) + teto de 10 envios
 * por sessão de página. fetch keepalive direto no tópico — sem dependências.
 * Nunca relança nem interfere no fluxo normal de erro do app.
 */
import type { App } from 'vue'
import { config } from './configService'

const DEDUPE_WINDOW_MS = 5 * 60 * 1000
const MAX_PER_PAGE = 10

const sent = new Map<string, number>()
let sentCount = 0

function topicUrl(): string {
  const t = config.app.ntfyErrorsTopic
  return t ? `https://ntfy.sh/${t}` : ''
}

function report(kind: string, message: string, detail: string): void {
  const url = topicUrl()
  if (!url || sentCount >= MAX_PER_PAGE) return

  const sig = `${kind}:${message}`.slice(0, 160)
  const now = Date.now()
  const last = sent.get(sig)
  if (last !== undefined && now - last < DEDUPE_WINDOW_MS) return
  sent.set(sig, now)
  sentCount += 1

  const body = [
    message.slice(0, 300),
    detail ? detail.slice(0, 400) : '',
    `pagina: ${window.location.pathname}`,
    `versao: ${config.app.appVersion}`,
    `ua: ${navigator.userAgent.slice(0, 90)}`,
  ].filter(Boolean).join('\n')

  try {
    // O reporter de erros NÃO pode passar pelo httpService — um erro do próprio
    // httpService recursaria no reporter, e o ntfy é externo (sem JWT/envelope).
    // @no-raw-axios-exception: transporte externo sem auth; httpService recursaria
    fetch(url, {
      method: 'POST',
      keepalive: true,
      headers: {
        Title: `[${window.location.hostname.split('.')[1] ?? 'front'}] ${kind}`,
        Tags: 'red_circle',
      },
      body,
    }).catch(() => { /* reporter nunca gera erro próprio */ })
  } catch {
    /* idem */
  }
}

export function initErrorReporting(app: App): void {
  if (!topicUrl()) return

  window.addEventListener('error', (event: ErrorEvent) => {
    report('erro js', event.message || 'erro sem mensagem', `${event.filename ?? ''}:${event.lineno ?? 0}`)
  })

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const r = event.reason
    const msg = r instanceof Error ? r.message : String(r)
    report('promise rejeitada', msg, r instanceof Error ? (r.stack ?? '').split('\n').slice(0, 3).join(' | ') : '')
  })

  const previous = app.config.errorHandler
  app.config.errorHandler = (err, instance, info) => {
    const msg = err instanceof Error ? err.message : String(err)
    const comp = (instance?.$options?.name as string | undefined) ?? 'componente'
    report('erro vue', msg, `${comp} — ${info}`)
    if (previous) previous(err, instance, info)
    else console.error('[errorReporting]', err)
  }
}
